<template>
  <div class="preview-link-share">
    <h6 v-if="showTitle">{{ $t('previewLink.title') }}</h6>
    <p v-if="showDescription" class="text-muted small mb-2">{{ $t('previewLink.description') }}</p>

    <div v-if="loading" class="text-center py-2">
      <b-spinner small></b-spinner>
    </div>

    <b-button
      v-else-if="!previewSlug"
      variant="primary"
      size="sm"
      @click="setSlug('enable')"
    >
      {{ $t('previewLink.create') }}
    </b-button>

    <div v-else>
      <b-input-group size="sm" class="mb-2">
        <b-form-input :value="previewUrl" readonly></b-form-input>
        <b-input-group-append>
          <b-button
            v-b-tooltip.hover
            :title="$t('buttons.copyLink')"
            variant="outline-secondary"
            @click="copyUrl"
          >
            <b-icon :icon="copied ? 'check' : 'clipboard'" aria-hidden="true"></b-icon>
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <b-button variant="outline-secondary" size="sm" class="mr-2" @click="confirmRegenerate">
        {{ $t('previewLink.regenerate') }}
      </b-button>
      <b-button variant="outline-danger" size="sm" @click="confirmDisable">
        {{ $t('previewLink.disable') }}
      </b-button>
    </div>

    <b-modal
        :id="confirmModalId"
        :ref="confirmModalId"
        footer-class="flex-nowrap"
        no-close-on-backdrop
        centered
    >
      <template #modal-header>
        <h3>{{ pendingAction === 'disable' ? $t('previewLink.disable') : $t('previewLink.regenerate') }}</h3>
      </template>
      <div class="delete-icon text-center">
        <b-icon icon="exclamation-triangle-fill" variant="danger" class="mt-5 mb-4"></b-icon>
        <div class="mt-2 mb-4">
          {{ pendingAction === 'disable'
            ? $t('previewLink.disableConfirm')
            : $t('previewLink.regenerateConfirm') }}
        </div>
      </div>
      <template #modal-footer="{ close }">
        <b-button
          class="p-3 border-right-light-grey"
          block
          @click="close()"
        >
          {{ $t('buttons.cancel') }}
        </b-button>
        <b-button
          variant="primary"
          class="p-3"
          block
          @click="confirmPendingAction"
        >
          {{ $t('buttons.ok') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import SetPreviewSlug from '@/graphQlQueries/mutations/setPreviewSlug';
import GetPagePreviewSlug from '@/graphQlQueries/queries/getPagePreviewSlug';
import GetProjectPreviewSlug from '@/graphQlQueries/queries/getProjectPreviewSlug';
import buildPreviewUrl from '@/utils/previewLink';

export default {
  name: 'PreviewLinkShare',
  props: {
    pageId: { type: Number, default: null },
    projectId: { type: Number, default: null },
    groupDomain: { type: String, required: true },
    slugPath: { type: String, required: true },
    pageType: { type: String, required: true },
    showTitle: { type: Boolean, default: true },
    showDescription: { type: Boolean, default: true },
  },
  data() {
    return {
      previewSlug: null,
      loading: true,
      copied: false,
      copyTimeout: null,
      pendingAction: null,
    };
  },
  computed: {
    previewUrl() {
      return buildPreviewUrl(this.groupDomain, this.slugPath, this.pageType, this.previewSlug);
    },
    confirmModalId() {
      return `preview-link-confirm-${this.pageId || 'p'}-${this.projectId || 'x'}`;
    },
  },
  async created() {
    try {
      if (this.pageId) {
        const { data } = await this.$apollo.query({
          query: GetPagePreviewSlug,
          variables: { pageId: this.pageId },
          fetchPolicy: 'network-only',
        });
        this.previewSlug = data.pages_by_pk?.preview_slug || null;
      } else if (this.projectId) {
        const { data } = await this.$apollo.query({
          query: GetProjectPreviewSlug,
          variables: { projectId: this.projectId },
          fetchPolicy: 'network-only',
        });
        this.previewSlug = data.projects_by_pk?.preview_slug || null;
      }
    } catch (err) {
      console.error('PreviewLinkShare: failed to load preview slug', err);
      this.$bvToast.toast(this.$t('previewLink.error'), {
        variant: 'danger', solid: true, autoHideDelay: 3000, noCloseButton: true,
      });
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async setSlug(action) {
      this.loading = true;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: SetPreviewSlug,
          variables: { object: { page_id: this.pageId, project_id: this.projectId, action } },
        });
        this.previewSlug = data.setPreviewSlug.preview_slug;
        this.$emit('slug-changed');
      } catch (err) {
        console.error('PreviewLinkShare: setPreviewSlug failed', err);
        this.$bvToast.toast(this.$t('previewLink.error'), {
          variant: 'danger', solid: true, autoHideDelay: 3000, noCloseButton: true,
        });
      } finally {
        this.loading = false;
      }
    },
    confirmRegenerate() {
      this.pendingAction = 'regenerate';
      this.$bvModal.show(this.confirmModalId);
    },
    confirmDisable() {
      this.pendingAction = 'disable';
      this.$bvModal.show(this.confirmModalId);
    },
    async confirmPendingAction() {
      const action = this.pendingAction;
      if (action) await this.setSlug(action);
      this.$bvModal.hide(this.confirmModalId);
    },
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.previewUrl);
        this.copied = true;
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
        this.copyTimeout = setTimeout(() => { this.copied = false; }, 2000);
        this.$bvToast.toast(this.$t('previewLink.copied'), {
          variant: 'success', solid: true, autoHideDelay: 2000, noCloseButton: true,
        });
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.delete-icon svg {
  font-size: 80px;
}
</style>
